import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DndContext, PointerSensor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { FaCog } from "react-icons/fa";
import {
  TbChartAreaLine,
  TbFingerprint,
  TbPlugConnected,
  TbServer2,
  TbCloudDollar,
  TbKey,
  TbEyeSearch,
} from "react-icons/tb";
import Title from "../common/Title";
import DesktopWindow from "./DesktopWindow";
import ArmaninoSSO from "./ArmaninoSSO";
import AuthenticatorForm from "../client/AuthenticatorForm";
import RunbookSystemGrid from "../runbookSystem/RunbookSystemGrid";
import RunbookSystemForm from "../runbookSystem/RunbookSystemForm";
import RunbookServerGrid from "../runbookServer/RunbookServerGrid";
import RunbookServerForm from "../runbookServer/RunbookServerForm";
import McpTokenGrid from "../mcpToken/McpTokenGrid";
import InspectRequestGrid from "../inspectRequest/InspectRequestGrid";
import AzureCostGrid from "../azureCost/AzureCostGrid";
import UsageAnalyticsOverview from "../usageAnalytics/product/UsageAnalyticsOverview";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useDesktopWindows, WINDOW_START_Y } from "../../hooks/useDesktopWindows";
import { useDesktopIconPositions } from "../../hooks/useDesktopIconPositions";
import armaninoLogo from "../../assets/armanino-llp.png";
import armaninoLogoDark from "../../assets/Armanino-logo-reversed.png";
import "./ProductHub.css";

// Armanino brand palette (src/custom.scss): $primary/$primary-dark/$primary-light (orange),
// $secondary/$secondary-dark/$secondary-light (olive green), alternated so adjacent icons differ.
const ICONS = [
  { id: "armanino-sso", label: "Armanino SSO", to: "/armanino-sso", Icon: TbFingerprint, opensWindow: true, color: "#cf7f00" },
  { id: "systems", label: "Runbook Systems", to: "/product/systems", Icon: TbPlugConnected, opensWindow: true, color: "#949300" },
  { id: "servers", label: "Runbook Servers", to: "/product/servers", Icon: TbServer2, opensWindow: true, color: "#b36d00" },
  { id: "azure-costs", label: "Azure Costs", to: "/product/azure-costs", Icon: TbCloudDollar, opensWindow: true, color: "#8a8800", maximize: true },
  { id: "mcp-tokens", label: "MCP Tokens", to: "/product/mcp-tokens", Icon: TbKey, opensWindow: true, color: "#e69500", width: 980, height: 700 },
  { id: "usage-analytics", label: "Usage Analytics", to: "/product/usage-analytics", Icon: TbChartAreaLine, opensWindow: true, color: "#a5a300", maximize: true },
  { id: "inspect-requests", label: "Inspect Requests", to: "/product/inspect-requests", Icon: TbEyeSearch, opensWindow: true, color: "#cf7f00", width: 980, height: 700 },
].sort((a, b) => a.label.localeCompare(b.label));

function getIconColor(id) {
  return ICONS.find((i) => i.id === id)?.color;
}

const AUTHENTICATOR_FORM_WINDOW_ID = "authenticator-form";
const SYSTEM_FORM_WINDOW_ID = "system-form";
const SERVER_FORM_WINDOW_ID = "server-form";

const WINDOW_Z_BASE = 500;
// The sticky app header sits above windows in z-index, so a window dragged past this
// point has its title bar hidden underneath it and becomes unreachable.
const WINDOW_MIN_Y = 65;

const STORAGE_KEY = "productHubIconPositions";

function DesktopIcon({ icon, position, isSelected, onSelect, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: icon.id });
  const { Icon } = icon;

  const style = {
    transform: `translate3d(${position.x + (transform?.x ?? 0)}px, ${position.y + (transform?.y ?? 0)}px, 0)`,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      className={`desktop-icon${isSelected ? " selected" : ""}${isDragging ? " dragging" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(icon.id);
      }}
      onDoubleClick={() => onOpen(icon)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(icon);
      }}
      aria-label={icon.label}
    >
      <div className="desktop-icon-glyph" style={{ "--tile-color": icon.color }}>
        <Icon />
      </div>
      <span className="desktop-icon-label">{icon.label}</span>
    </div>
  );
}

export default function ProductHub({ user }) {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const containerRef = useRef(null);
  const watermarkRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const {
    openWindows,
    openWindow,
    openOrUpdateWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    toggleMaximizeWindow,
    handleWindowResize,
    handleWindowDragEnd,
  } = useDesktopWindows({ minY: WINDOW_MIN_Y });

  // Pops the window's route open in its own browser window (reusing the same session
  // cookies) and closes the floating copy, since the two would otherwise show duplicate
  // live state for the same feature.
  const handlePopOutWindow = (win) => {
    if (!win.to) return;
    window.open(win.to, `popout-${win.id}`, `width=${win.width},height=${win.height},left=${win.x},top=${win.y}`);
    closeWindow(win.id);
  };
  const { positions, handleDragEnd, handleReset } = useDesktopIconPositions({
    icons: ICONS,
    storageKey: STORAGE_KEY,
    containerRef,
    avoidRef: watermarkRef,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );
  const windowSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const openAuthenticatorWindow = ({ mode, authId, clientId }) => {
    const title = mode === "edit" ? "Edit Authenticator" : "Add Authenticator";
    openOrUpdateWindow(AUTHENTICATOR_FORM_WINDOW_ID, {
      title,
      Icon: TbFingerprint,
      color: getIconColor("armanino-sso"),
      meta: { mode, authId, clientId },
    });
  };

  const openSystemFormWindow = ({ mode, system }) => {
    const title =
      mode === "edit" ? "Edit Runbook System" : mode === "view" ? "View Runbook System" : "Add Runbook System";
    openOrUpdateWindow(SYSTEM_FORM_WINDOW_ID, {
      title,
      Icon: TbPlugConnected,
      color: getIconColor("systems"),
      meta: { mode, system },
      // Larger than the default window, and offset further right so it doesn't
      // land directly on top of the Runbook Systems window that opened it.
      sizing: () => {
        const width = Math.min(760, window.innerWidth - 32);
        const height = Math.min(720, window.innerHeight - 96);
        const x = Math.min(780, window.innerWidth - width - 16);
        return { x, y: WINDOW_START_Y, width, height };
      },
    });
  };

  const openServerFormWindow = ({ mode, serverName }) => {
    const title = mode === "edit" ? "Edit Runbook Server" : "Add Runbook Server";
    openOrUpdateWindow(SERVER_FORM_WINDOW_ID, {
      title,
      Icon: TbServer2,
      color: getIconColor("servers"),
      meta: { mode, serverName },
    });
  };

  const renderWindowBody = (win) => {
    if (win.id === "armanino-sso") {
      return (
        <ArmaninoSSO
          user={user}
          hideTitle
          onEditAuthenticator={(authId) => openAuthenticatorWindow({ mode: "edit", authId })}
          onAddAuthenticator={() => openAuthenticatorWindow({ mode: "add" })}
        />
      );
    }
    if (win.id === AUTHENTICATOR_FORM_WINDOW_ID) {
      return (
        <AuthenticatorForm
          hideTitle
          presetAuthId={win.meta?.authId}
          presetClientId={win.meta?.clientId}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "systems") {
      return (
        <RunbookSystemGrid
          hideTitle
          onEditSystem={(system) => openSystemFormWindow({ mode: "edit", system })}
          onAddSystem={() => openSystemFormWindow({ mode: "add" })}
          onViewSystem={(system) => openSystemFormWindow({ mode: "view", system })}
        />
      );
    }
    if (win.id === SYSTEM_FORM_WINDOW_ID) {
      return (
        <RunbookSystemForm
          hideTitle
          readOnly={win.meta?.mode === "view"}
          presetSystem={win.meta?.system}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
          onEditRequest={(system) => openSystemFormWindow({ mode: "edit", system })}
          onDeleted={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "servers") {
      return (
        <RunbookServerGrid
          hideTitle
          onEditServer={(serverName) => openServerFormWindow({ mode: "edit", serverName })}
          onAddServer={() => openServerFormWindow({ mode: "add" })}
        />
      );
    }
    if (win.id === SERVER_FORM_WINDOW_ID) {
      return (
        <RunbookServerForm
          hideTitle
          presetServerName={win.meta?.serverName}
          onSaveSuccess={() => closeWindow(win.id)}
          onCancel={() => closeWindow(win.id)}
        />
      );
    }
    if (win.id === "mcp-tokens") {
      return <McpTokenGrid hideTitle />;
    }
    if (win.id === "inspect-requests") {
      return <InspectRequestGrid hideTitle user={user} />;
    }
    if (win.id === "azure-costs") {
      return <AzureCostGrid hideTitle />;
    }
    if (win.id === "usage-analytics") {
      return <UsageAnalyticsOverview user={user} hideTitle />;
    }
    return null;
  };

  const handleOpen = (icon) => {
    if (icon.opensWindow) {
      openWindow(icon);
    } else {
      navigate(icon.to);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Title text="Product Team" icon={<FaCog className="mb-1" />} />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-none d-md-inline-block"
          onClick={handleReset}
        >
          Reset Layout
        </button>
      </div>

      <div className="product-desktop d-none d-md-block" ref={containerRef} onClick={() => setSelectedId(null)}>
        <img
          ref={watermarkRef}
          className="product-desktop-watermark"
          src={darkMode ? armaninoLogoDark : armaninoLogo}
          alt=""
          aria-hidden="true"
        />
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          {ICONS.map((icon) => (
            <DesktopIcon
              key={icon.id}
              icon={icon}
              position={positions[icon.id]}
              isSelected={selectedId === icon.id}
              onSelect={setSelectedId}
              onOpen={handleOpen}
            />
          ))}
        </DndContext>
      </div>

      <DndContext sensors={windowSensors} onDragEnd={handleWindowDragEnd}>
        {openWindows.map((win) => (
          <DesktopWindow
            key={win.id}
            id={win.id}
            title={win.title}
            icon={<win.Icon />}
            accentColor={win.color}
            position={{ x: win.x, y: win.y }}
            size={{ width: win.width, height: win.height }}
            zIndex={WINDOW_Z_BASE + win.zIndex}
            minY={WINDOW_MIN_Y}
            isMinimized={win.isMinimized}
            isMaximized={win.isMaximized}
            onClose={closeWindow}
            onFocus={() => focusWindow(win.id)}
            onResize={handleWindowResize}
            onMinimize={minimizeWindow}
            onMaximizeToggle={toggleMaximizeWindow}
            onPopOut={win.to ? () => handlePopOutWindow(win) : undefined}
          >
            {renderWindowBody(win)}
          </DesktopWindow>
        ))}
      </DndContext>

      <div className="product-desktop-mobile d-md-none">
        {ICONS.map(({ id, label, to, Icon, color }) => (
          <button
            key={id}
            type="button"
            className="product-desktop-mobile-item"
            onClick={() => navigate(to)}
          >
            <span className="desktop-icon-glyph" style={{ "--tile-color": color }}>
              <Icon />
            </span>
            <span className="desktop-icon-label">{label}</span>
          </button>
        ))}
      </div>
    </>
  );
}
